import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, Search } from 'lucide-react'
import { personFromName, ticketDelay } from '@/components/tickets/data'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { api } from '@/services/api'

export type TicketPeriod = 'all' | '30d' | 'month' | 'quarter' | 'year'

export const TICKET_PERIODS: Array<{ id: TicketPeriod; label: string; short: string }> = [
  { id: 'all', label: 'Toutes les périodes', short: 'Tout' },
  { id: '30d', label: '30 derniers jours', short: '30 j' },
  { id: 'month', label: 'Ce mois', short: 'Mois' },
  { id: 'quarter', label: 'Ce trimestre', short: 'Trimestre' },
  { id: 'year', label: 'Cette année', short: 'Année' },
]

/** Tous les chefs de projet du ticket (principal en premier). */
function chefIdsOf(ticket: { chef_projet_id: string | null; chef_projet_ids?: string[] }): string[] {
  if (ticket.chef_projet_ids?.length) return ticket.chef_projet_ids
  return ticket.chef_projet_id ? [ticket.chef_projet_id] : []
}

interface ApiTicket {
  id: number
  titre: string
  project_id: number
  chef_projet_id: string | null
  chef_projet_ids?: string[]
  assignee_ids: string[]
  echeance: string
  status: string
  done_at: string | null
  reviewed_at: string | null
  time_spent_days: number | null
  cp_time_spent_days: number | null
}

interface ApiUser {
  id: string
  nom: string
  email: string
  role: string
}

interface ApiProject {
  id: number
  nom: string
}

interface ListResponse<T> {
  data: T[]
}

const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  todo: { label: 'À faire', className: 'bg-gray-100 text-gray-600' },
  doing: { label: 'En cours', className: 'bg-sky-100 text-sky-700' },
  review: { label: 'En révision', className: 'bg-amber-100 text-amber-800' },
  done: { label: 'Fait', className: 'bg-emerald-100 text-emerald-700' },
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[]
  if (body && typeof body === 'object' && Array.isArray((body as ListResponse<T>).data)) {
    return (body as ListResponse<T>).data
  }
  return []
}

function parseDay(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

function startOfToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function dayDiff(later: Date, earlier: Date): number {
  return Math.round((later.getTime() - earlier.getTime()) / 86_400_000)
}

function inPeriod(due: string, period: TicketPeriod): boolean {
  if (period === 'all') return true
  const date = parseDay(due)
  const today = startOfToday()
  if (period === '30d') {
    const from = new Date(today)
    from.setDate(from.getDate() - 30)
    return date >= from && date <= today
  }
  if (period === 'month') {
    return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth()
  }
  if (period === 'quarter') {
    const quarter = Math.floor(today.getMonth() / 3)
    return date.getFullYear() === today.getFullYear() && Math.floor(date.getMonth() / 3) === quarter
  }
  return date.getFullYear() === today.getFullYear()
}

function formatDeadline(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}/${year.slice(2)}`
}

function formatDays(value: number, digits: number): string {
  return value.toFixed(digits)
}

function formatDetailTime(value: number | null): string {
  if (value == null || value <= 0) return '—'
  return Number.isInteger(value) ? `${value}j` : `${formatDays(value, 1)}j`
}

function formatTicketLoggedTime(ticket: ApiTicket): string {
  const dev = ticket.time_spent_days || 0
  const cp = ticket.cp_time_spent_days || 0
  if (dev <= 0 && cp <= 0) return '—'
  if (cp <= 0) return formatDetailTime(dev)
  if (dev <= 0) return `CP ${formatDetailTime(cp)}`
  return `${formatDetailTime(dev)} · CP ${formatDetailTime(cp)}`
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name
}

function delayLabel(ticket: ApiTicket): { text: string; late: boolean } {
  return ticketDelay({
    status: ticket.status,
    dueDate: ticket.echeance,
    doneAt: ticket.done_at,
    reviewedAt: ticket.reviewed_at,
  })
}

/** Ticket passé en révision après son échéance (reviewed_at > echeance). */
function isLateToReview(ticket: ApiTicket): boolean {
  if (!ticket.reviewed_at) return false
  return dayDiff(parseDay(ticket.reviewed_at), parseDay(ticket.echeance)) > 0
}

interface PersonStats {
  id: string
  tickets: number
  done: number
  time: number
  late: number
  lateReview: number
  lateTickets: ApiTicket[]
}

function SearchableFilter({
  label,
  searchPlaceholder,
  value,
  onChange,
  options,
}: {
  label: string
  searchPlaceholder: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <div className="w-56">
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={label}
        searchPlaceholder={searchPlaceholder}
        className={`h-9 rounded-full bg-gray-50 text-sm ${
          value ? 'border-cyan-200 text-gray-900' : 'border-gray-200'
        }`}
      />
    </div>
  )
}

function ColumnFilter({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ id: string; label: string }>
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`h-9 max-w-[12rem] truncate rounded-full border bg-gray-50 px-3 text-sm outline-none focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100 ${
        value ? 'border-cyan-200 text-gray-900' : 'border-gray-200 text-gray-500'
      }`}
    >
      <option value="">{label}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

function emptyPerson(id: string): PersonStats {
  return { id, tickets: 0, done: 0, time: 0, late: 0, lateReview: 0, lateTickets: [] }
}

export function TicketTimeTracking({ period }: { period: TicketPeriod }) {
  const [tickets, setTickets] = useState<ApiTicket[]>([])
  const [users, setUsers] = useState<ApiUser[]>([])
  const [chefs, setChefs] = useState<ApiUser[]>([])
  const [projects, setProjects] = useState<ApiProject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [projectFilter, setProjectFilter] = useState('')
  const [devFilter, setDevFilter] = useState('')
  const [chefFilter, setChefFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [delayFilter, setDelayFilter] = useState('')
  const [lateDialog, setLateDialog] = useState<{ title: string; tickets: ApiTicket[] } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      api.get<ListResponse<ApiTicket>>('/tickets'),
      api.get<ListResponse<ApiUser>>('/users', { internal: 'true' }),
      api.get<ListResponse<ApiUser>>('/users/project-managers'),
      api.get<ListResponse<ApiProject>>('/projects'),
    ])
      .then(([ticketsResponse, usersResponse, chefsResponse, projectsResponse]) => {
        if (cancelled) return
        setTickets(unwrapList<ApiTicket>(ticketsResponse.data))
        setUsers(unwrapList<ApiUser>(usersResponse.data))
        setChefs(unwrapList<ApiUser>(chefsResponse.data))
        setProjects(unwrapList<ApiProject>(projectsResponse.data))
        setError('')
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger le suivi des tickets.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const userById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users])
  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project.nom])), [projects])

  const visibleTickets = useMemo(
    () => tickets.filter((ticket) => inPeriod(ticket.echeance, period)),
    [tickets, period]
  )

  const stats = useMemo(() => {
    const done = visibleTickets.filter((ticket) => ticket.status === 'done')
    const totalDays = done.reduce(
      (sum, ticket) => sum + (ticket.time_spent_days || 0) + (ticket.cp_time_spent_days || 0),
      0
    )
    const late = visibleTickets.filter((ticket) => delayLabel(ticket).late).length
    const lateReview = visibleTickets.filter((ticket) => isLateToReview(ticket)).length
    const completion = visibleTickets.length === 0 ? 0 : Math.round((done.length / visibleTickets.length) * 100)
    const average = done.length === 0 ? null : totalDays / done.length
    return { done: done.length, total: visibleTickets.length, totalDays, late, lateReview, completion, average }
  }, [visibleTickets])

  const developers = useMemo(() => {
    const rows = new Map<string, PersonStats>()
    for (const ticket of visibleTickets) {
      const assignees = ticket.assignee_ids.filter((id) => userById.has(id))
      const late = delayLabel(ticket).late
      const chefId = ticket.chef_projet_id
      if (assignees.length === 0) {
        if (late && (ticket.status === 'review' || ticket.status === 'done') && chefId && userById.has(chefId)) {
          const row = rows.get(chefId) ?? emptyPerson(chefId)
          row.late += 1
          row.lateTickets.push(ticket)
          rows.set(chefId, row)
        }
        continue
      }
      const share =
        ticket.status === 'done' && ticket.time_spent_days
          ? ticket.time_spent_days / assignees.length
          : 0
      const lateReview = isLateToReview(ticket)
      for (const id of assignees) {
        const row = rows.get(id) ?? emptyPerson(id)
        row.tickets += 1
        if (ticket.status === 'done') row.done += 1
        row.time += share
        if (late) {
          row.late += 1
          row.lateTickets.push(ticket)
        }
        if (lateReview) row.lateReview += 1
        rows.set(id, row)
      }
      const cpDays = ticket.status === 'done' ? ticket.cp_time_spent_days || 0 : 0
      if (chefId && cpDays > 0 && userById.has(chefId)) {
        const row = rows.get(chefId) ?? emptyPerson(chefId)
        if (!assignees.includes(chefId)) {
          row.tickets += 1
          row.done += 1
        }
        row.time += cpDays
        rows.set(chefId, row)
      }
    }
    return [...rows.values()]
      .map((row) => {
        const user = userById.get(row.id)
        const person = personFromName(row.id, user?.nom || 'Sans nom')
        return {
          ...row,
          name: person.name,
          email: user?.email || '',
          initial: person.initial,
          color: person.color,
          average: row.done > 0 ? row.time / row.done : null,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  }, [visibleTickets, userById])

  const projectOptions = useMemo(
    () =>
      [...projects]
        .map((project) => ({ value: String(project.id), label: project.nom }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [projects]
  )

  const devOptions = useMemo(
    () =>
      users
        .filter((user) => user.role === 'admin' || user.role === 'super_admin')
        .map((user) => ({ value: user.id, label: user.nom.trim() || user.email }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [users]
  )

  const chefOptions = useMemo(
    () =>
      [...chefs]
        .map((user) => ({ value: user.id, label: user.nom.trim() || user.email }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [chefs]
  )

  const statusOptions = useMemo(
    () =>
      Object.entries(STATUS_STYLE).map(([id, style]) => ({
        id,
        label: style.label,
      })),
    []
  )

  const filtersActive = Boolean(projectFilter || devFilter || chefFilter || statusFilter || delayFilter)

  const detailTickets = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return visibleTickets.filter((ticket) => {
      if (projectFilter && String(ticket.project_id) !== projectFilter) return false
      if (devFilter && !ticket.assignee_ids.includes(devFilter)) return false
      if (chefFilter && !chefIdsOf(ticket).includes(chefFilter)) return false
      if (statusFilter && ticket.status !== statusFilter) return false
      if (delayFilter === 'late' && !delayLabel(ticket).late) return false
      if (delayFilter === 'ontime' && delayLabel(ticket).late) return false
      if (!needle) return true
      const project = projectById.get(ticket.project_id) || ''
      const people = ticket.assignee_ids.map((id) => userById.get(id)?.nom || '').join(' ')
      const chef = chefIdsOf(ticket).map((id) => userById.get(id)?.nom || '').join(' ')
      const status = STATUS_STYLE[ticket.status]?.label || ticket.status
      return `${ticket.titre} ${project} ${people} ${chef} ${status}`.toLowerCase().includes(needle)
    })
  }, [visibleTickets, query, projectFilter, devFilter, chefFilter, statusFilter, delayFilter, projectById, userById])

  if (loading) {
    return <PageSkeleton />
  }

  if (error) {
    return <div className="py-12 text-center text-sm text-rose-600">{error}</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl bg-[#f3fbf9] p-5">
          <div className="flex items-center justify-between">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-cyan-600">
              <Clock className="h-4 w-4" />
            </span>
            <span className="text-xs text-gray-500">
              {stats.done} terminé{stats.done > 1 ? 's' : ''}
            </span>
          </div>
          <p className="mt-5 text-sm text-gray-500">Jours totaux passés</p>
          <p className="mt-1 text-[2rem] font-semibold leading-none tracking-tight text-gray-950 tabular-nums">
            {formatDays(stats.totalDays, 1)}
            <span className="ml-1 text-lg font-medium text-gray-500">j</span>
          </p>
          <p className="mt-3 text-sm text-gray-500">
            {stats.average == null ? 'Aucun ticket terminé' : `Moy. ${formatDays(stats.average, 1)}j / ticket terminé`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            if (stats.late === 0) return
            setLateDialog({
              title: 'Tickets en retard',
              tickets: [...visibleTickets.filter((ticket) => delayLabel(ticket).late)].sort((a, b) =>
                a.echeance.localeCompare(b.echeance)
              ),
            })
          }}
          className={`w-full rounded-2xl border border-gray-200/80 bg-white p-5 text-left ${
            stats.late > 0 ? 'cursor-pointer transition hover:border-rose-200 hover:bg-rose-50/30' : 'cursor-default'
          }`}
        >
          <span className={`flex h-8 w-8 items-center justify-center rounded-full ${stats.late > 0 ? 'bg-rose-50 text-rose-500' : 'bg-gray-50 text-gray-400'}`}>
            <AlertTriangle className="h-4 w-4" />
          </span>
          <p className="mt-5 text-sm text-gray-500">Tickets en retard</p>
          <p className={`mt-1 text-[2rem] font-semibold leading-none tracking-tight tabular-nums ${stats.late > 0 ? 'text-rose-600' : 'text-gray-950'}`}>
            {stats.late}
          </p>
          <p className="mt-3 text-sm text-gray-500">
            {stats.late > 0 ? 'Retards de la période · voir la liste' : 'Retards de la période'}
          </p>
        </button>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-5">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full ${stats.lateReview > 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-400'}`}>
            <AlertTriangle className="h-4 w-4" />
          </span>
          <p className="mt-5 text-sm text-gray-500">Révision hors délai</p>
          <p className={`mt-1 text-[2rem] font-semibold leading-none tracking-tight tabular-nums ${stats.lateReview > 0 ? 'text-amber-700' : 'text-gray-950'}`}>
            {stats.lateReview}
          </p>
          <p className="mt-3 text-sm text-gray-500">Mis en révision après échéance</p>
        </div>

        <div className="rounded-2xl border border-gray-200/80 bg-white p-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />
          </span>
          <p className="mt-5 text-sm text-gray-500">Taux de complétion</p>
          <p className="mt-1 text-[2rem] font-semibold leading-none tracking-tight text-gray-950 tabular-nums">
            {stats.completion}
            <span className="ml-1 text-lg font-medium text-gray-500">%</span>
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500"
              style={{ width: `${stats.completion}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {stats.done} / {stats.total} ticket{stats.total > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white">
        <div className="px-5 pt-5">
          <h2 className="text-base font-semibold text-gray-950">Par développeur</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Temps total, moyenne par ticket terminé, retards, révision hors délai
          </p>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-y border-gray-100 text-left text-xs font-medium text-gray-400">
                <th className="px-5 py-2.5 font-medium">Développeur</th>
                <th className="px-3 py-2.5 text-right font-medium">Tickets</th>
                <th className="px-3 py-2.5 text-right font-medium">Terminés</th>
                <th className="px-3 py-2.5 font-medium">Avancement</th>
                <th className="px-3 py-2.5 text-right font-medium">Temps total</th>
                <th className="px-3 py-2.5 text-right font-medium">Moy. / ticket</th>
                <th className="px-3 py-2.5 text-right font-medium">Retards</th>
                <th className="px-5 py-2.5 text-right font-medium">Révision hors délai</th>
              </tr>
            </thead>
            <tbody>
              {developers.map((developer) => {
                const progress = developer.tickets === 0 ? 0 : Math.round((developer.done / developer.tickets) * 100)
                return (
                  <tr key={developer.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
                          style={{ backgroundColor: developer.color }}
                        >
                          {developer.initial}
                        </span>
                        <span>
                          <span className="block font-medium text-gray-950">{developer.name}</span>
                          {developer.email ? (
                            <span className="block text-xs text-gray-400">{developer.email}</span>
                          ) : null}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">{developer.tickets}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">{developer.done}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-cyan-400"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-gray-400">{progress}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-medium tabular-nums text-gray-950">
                      {developer.time > 0 ? `${formatDays(developer.time, 1)}j` : '—'}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                      {developer.average == null ? '—' : `${formatDays(developer.average, 1)}j`}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {developer.late > 0 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setLateDialog({
                              title: `Retards — ${developer.name}`,
                              tickets: [...developer.lateTickets].sort((a, b) => a.echeance.localeCompare(b.echeance)),
                            })
                          }
                          className="inline-flex min-w-6 cursor-pointer justify-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium tabular-nums text-rose-600 hover:bg-rose-100"
                        >
                          {developer.late}
                        </button>
                      ) : (
                        <span className="text-gray-300">0</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {developer.lateReview > 0 ? (
                        <span className="inline-flex min-w-6 justify-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium tabular-nums text-amber-700">
                          {developer.lateReview}
                        </span>
                      ) : (
                        <span className="text-gray-300">0</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {developers.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500">Aucun développeur sur cette période</p>
        ) : null}
      </section>

      <section className="rounded-2xl border border-gray-200/80 bg-white">
        <div className="flex flex-col gap-3 px-5 pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-950">Détail des tickets</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {detailTickets.length} ticket{detailTickets.length > 1 ? 's' : ''}
                {query.trim() || filtersActive ? ` sur ${visibleTickets.length}` : ''}
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher un ticket"
                className="h-9 w-full rounded-full border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
          </div>
          <div className="relative z-20 flex flex-wrap items-center gap-2">
            <SearchableFilter
              label="Projet"
              searchPlaceholder="Rechercher un projet"
              value={projectFilter}
              onChange={setProjectFilter}
              options={projectOptions}
            />
            <SearchableFilter
              label="Développeur"
              searchPlaceholder="Rechercher un développeur"
              value={devFilter}
              onChange={setDevFilter}
              options={devOptions}
            />
            <SearchableFilter
              label="Chef de projet"
              searchPlaceholder="Rechercher un chef de projet"
              value={chefFilter}
              onChange={setChefFilter}
              options={chefOptions}
            />
            <ColumnFilter label="Statut" value={statusFilter} onChange={setStatusFilter} options={statusOptions} />
            <ColumnFilter
              label="Délai"
              value={delayFilter}
              onChange={setDelayFilter}
              options={[
                { id: 'late', label: 'En retard' },
                { id: 'ontime', label: 'Dans les temps' },
              ]}
            />
            {filtersActive ? (
              <button
                type="button"
                onClick={() => {
                  setProjectFilter('')
                  setDevFilter('')
                  setChefFilter('')
                  setStatusFilter('')
                  setDelayFilter('')
                }}
                className="h-9 rounded-full px-3 text-sm font-medium text-gray-500 hover:text-gray-800"
              >
                Effacer
              </button>
            ) : null}
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-b-2xl">
          <table className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="border-y border-gray-100 text-left text-xs font-medium text-gray-400">
                <th className="px-5 py-2.5 font-medium">Ticket</th>
                <th className="px-3 py-2.5 font-medium">Projet</th>
                <th className="px-3 py-2.5 font-medium">Développeur</th>
                <th className="px-3 py-2.5 font-medium">Chef de projet</th>
                <th className="px-3 py-2.5 font-medium">Deadline</th>
                <th className="px-3 py-2.5 font-medium">Statut</th>
                <th className="px-3 py-2.5 text-right font-medium">Temps passé</th>
                <th className="px-5 py-2.5 text-right font-medium">Délai</th>
              </tr>
            </thead>
            <tbody>
              {detailTickets.map((ticket) => {
                const assignees = ticket.assignee_ids
                  .map((id) => userById.get(id))
                  .filter((user): user is ApiUser => Boolean(user))
                const lead = assignees[0]
                const leadPerson = lead ? personFromName(lead.id, lead.nom) : null
                const chefs = chefIdsOf(ticket)
                  .map((id) => userById.get(id))
                  .filter((user): user is ApiUser => Boolean(user))
                const chef = chefs[0]
                const status = STATUS_STYLE[ticket.status] ?? {
                  label: ticket.status,
                  className: 'bg-gray-100 text-gray-600',
                }
                const delay = delayLabel(ticket)
                return (
                  <tr key={ticket.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80">
                    <td className="max-w-[220px] truncate px-5 py-3 font-medium text-gray-950">{ticket.titre}</td>
                    <td className="max-w-[180px] truncate px-3 py-3 text-gray-500">
                      {projectById.get(ticket.project_id) || '—'}
                    </td>
                    <td className="px-3 py-3">
                      {leadPerson ? (
                        <span className="inline-flex items-center gap-2 text-gray-800">
                          <span
                            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                            style={{ backgroundColor: leadPerson.color }}
                          >
                            {leadPerson.initial}
                          </span>
                          {firstName(leadPerson.name)}
                          {assignees.length > 1 ? (
                            <span className="text-xs text-gray-400">+{assignees.length - 1}</span>
                          ) : null}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-700" title={chefs.map((user) => user.nom).join(', ')}>
                      {chef ? firstName(chef.nom) : '—'}
                      {chefs.length > 1 ? <span className="ml-1 text-xs text-gray-400">+{chefs.length - 1}</span> : null}
                    </td>
                    <td className="px-3 py-3 tabular-nums text-gray-600">{formatDeadline(ticket.echeance)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                      {ticket.status === 'done' ? formatTicketLoggedTime(ticket) : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {delay.late ? (
                        <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium tabular-nums text-rose-600">
                          {delay.text}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {detailTickets.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500">
            {query.trim() || filtersActive
              ? 'Aucun ticket ne correspond aux filtres'
              : 'Aucun ticket sur cette période'}
          </p>
        ) : null}
      </section>

      <Dialog open={lateDialog !== null} onOpenChange={(open) => !open && setLateDialog(null)}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{lateDialog?.title}</DialogTitle>
            <DialogDescription>
              {lateDialog?.tickets.length ?? 0} ticket{(lateDialog?.tickets.length ?? 0) > 1 ? 's' : ''} avec une échéance dépassée
            </DialogDescription>
          </DialogHeader>
          <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {lateDialog?.tickets.map((ticket) => {
              const status = STATUS_STYLE[ticket.status] ?? {
                label: ticket.status,
                className: 'bg-gray-100 text-gray-600',
              }
              const delay = delayLabel(ticket)
              const chefNames = chefIdsOf(ticket)
                .map((id) => userById.get(id))
                .filter((user): user is ApiUser => Boolean(user))
                .map((user) => firstName(user.nom))
              return (
                <li key={ticket.id} className="rounded-xl border border-gray-100 px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium text-gray-950">{ticket.titre}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{projectById.get(ticket.project_id) || '—'}</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Échéance {formatDeadline(ticket.echeance)}
                    {delay.late ? ` · ${delay.text}` : ''}
                    {chefNames.length ? ` · ${chefNames.join(', ')}` : ''}
                  </p>
                </li>
              )
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  )
}
